import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Download } from 'lucide-react';
import { PageHeader } from '../../../components/ui/PageHeader/PageHeader';
import { Card } from '../../../components/ui/Card/Card';
import { Button } from '../../../components/ui/Button/Button';
import { DatePicker } from '../../../components/form/DatePicker';
import { Select } from '../../../components/form/Select';
import { useToast } from '../../../hooks/useToast';
import styles from '../../receipts/ReceiptDirectCreatePage.module.css';

const today = new Date().toISOString().slice(0, 10);

export function ReceiptsBulkExportPage(): JSX.Element {
  const navigate = useNavigate();
  const toast = useToast();

  const [dateFrom, setDateFrom] = useState(today);
  const [dateTo, setDateTo] = useState(today);
  const [format, setFormat] = useState<'pdf' | 'csv' | 'zip'>('zip');
  const [submitting, setSubmitting] = useState(false);

  function handleExport() {
    if (!dateFrom || !dateTo) {
      toast.error('Pick a start and end date.');
      return;
    }
    if (dateFrom > dateTo) {
      toast.error('Start date must be before end date.');
      return;
    }
    setSubmitting(true);
    // The bulk-export queue worker is documented in the server templates
    // README but the endpoint is not yet exposed. When it lands the
    // request will go to POST /api/v1/collections/receipts/bulk-export with this payload.
    toast.info(
      'Bulk export will be queued when the server endpoint is enabled. Use the receipts list filters in the meantime.',
    );
    setSubmitting(false);
  }

  return (
    <div className={styles.page}>
      <PageHeader
        title="Bulk Export Receipts"
        actions={
          <Button variant="secondary" onClick={() => navigate('/collections/receipts')}>
            Back to receipts
          </Button>
        }
      />

      <Card title="Export options">
        <div className={styles.grid2}>
          <DatePicker
            label="Date from"
            value={dateFrom}
            onChange={(e) => setDateFrom(e.target.value)}
          />
          <DatePicker
            label="Date to"
            value={dateTo}
            onChange={(e) => setDateTo(e.target.value)}
          />
          <Select
            label="Format"
            value={format}
            onChange={(e) => setFormat(e.target.value as 'pdf' | 'csv' | 'zip')}
            options={[
              { value: 'zip', label: 'ZIP archive (PDFs)' },
              { value: 'pdf', label: 'Single PDF' },
              { value: 'csv', label: 'CSV summary' },
            ]}
          />
        </div>
      </Card>

      <Card>
        <Button
          variant="primary"
          icon={Download}
          loading={submitting}
          onClick={handleExport}
        >
          Queue export
        </Button>
      </Card>
    </div>
  );
}
